import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Lock } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { formatApiError } from "../lib/api";

export default function ResetPassword() {
  const { t } = useI18n();
  const { resetPassword } = useAuth();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError(t("auth.passwordTooShort")); return; }
    if (password !== confirm) { setError(t("auth.passwordMismatch")); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-[#F8FAF9] to-teal-50">
      <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-200 shadow-xl p-8 cm-fade-up">
        <Link to="/" className="flex items-center justify-center mb-6">
          <img src="/logo.png" alt="CANARIAS MARKET" className="h-16 w-auto object-contain" />
        </Link>

        {done ? (
          <div className="text-center" data-testid="reset-success">
            <span className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-emerald-50 text-emerald-600 mb-4"><CheckCircle2 className="h-9 w-9" /></span>
            <h1 className="font-display font-bold text-2xl">{t("auth.resetSuccess")}</h1>
            <p className="text-sm text-slate-500 mt-2">{t("auth.resetSuccessDesc")}</p>
            <Button data-testid="reset-go-login" onClick={() => navigate("/auth")} className="w-full mt-6 h-11 bg-[#008080] hover:bg-[#006666]">{t("verify.goLogin")}</Button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <span className="h-14 w-14 mx-auto grid place-items-center rounded-2xl bg-teal-50 text-[#008080] mb-3"><Lock className="h-7 w-7" /></span>
              <h1 className="font-display font-bold text-2xl">{t("auth.resetTitle")}</h1>
              <p className="text-sm text-slate-500 mt-1">{t("auth.resetDesc")}</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>{t("auth.newPassword")}</Label>
                <Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" /></div>
              <div><Label>{t("auth.confirmPassword")}</Label>
                <Input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1" /></div>
              {error && <p data-testid="reset-error" className="text-sm text-rose-600">{error}</p>}
              <Button data-testid="reset-submit-btn" type="submit" disabled={loading || !token} className="w-full h-11 bg-[#008080] hover:bg-[#006666]">
                {loading ? t("common.loading") : t("auth.resetCta")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
